import { create } from "zustand";
import { palettes } from "./antd-theme";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "theme-mode";

function readInitialMode(): ThemeMode {
	const stored = localStorage.getItem(STORAGE_KEY);
	if (stored === "light" || stored === "dark") {
		return stored;
	}
	return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Mirrors the active palette onto the document as CSS variables so Tailwind
 * utilities and plain CSS can theme themselves alongside Ant Design.
 */
function applyMode(mode: ThemeMode) {
	const palette = palettes[mode];
	const root = document.documentElement;

	root.dataset.theme = mode;
	root.style.colorScheme = mode;

	for (const [key, value] of Object.entries(palette)) {
		root.style.setProperty(`--c-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`, value);
	}
}

interface ThemeState {
	mode: ThemeMode;
	setMode: (mode: ThemeMode) => void;
	toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
	mode: readInitialMode(),
	setMode: (mode) => {
		localStorage.setItem(STORAGE_KEY, mode);
		applyMode(mode);
		set({ mode });
	},
	toggle: () => get().setMode(get().mode === "dark" ? "light" : "dark"),
}));

// Paint the initial palette before React mounts to avoid a flash of the wrong theme.
applyMode(useThemeStore.getState().mode);
